import React, { useEffect, useState } from 'react';
import II18 from '../translate/II18';
import { Icon, ListInput } from 'framework7-react';

import PropTypes from 'prop-types';
import { phoneIsValid, userEmailOrMobileIsValid } from '../helpers/utils';
import 'react-international-phone/style.css';
import {
  defaultCountries,
  FlagImage,
  getCountry,
  guessCountryByPartialPhoneNumber,
  parseCountry
} from 'react-international-phone';
import { useDetectClickOutside } from 'react-detect-click-outside';

const getFullCountry = iso2 =>
  getCountry({
    value: iso2,
    field: 'iso2',
    countries: defaultCountries
  });

const updateCurrentNumberOrEmail = (v, country) => {
  if (phoneIsValid(v)) {
    if (v.indexOf('00') === 0) v = '+' + v.slice(2);
    if (v.indexOf('0') === 0) v = v.slice(1);
    return (v?.indexOf('+') === -1 ? `+${country.dialCode}` : '') + v;
  }
  return v;
};

export default function EmailPhoneInput(props) {
  const defaultCountryIso2 =
    props.region || navigator.language.slice(-2).toLowerCase();

  const [currentCountry, setCurrentCountry] = useState(
    guessCountryByPartialPhoneNumber({
      phone: props.value,
      countries: defaultCountries,
      currentCountryIso2: defaultCountryIso2
    }).country || getFullCountry(defaultCountryIso2)
  );

  const [currentValue, setCurrentValue] = useState(
    updateCurrentNumberOrEmail(props.value || '', currentCountry)
  );

  const [countrySelectOpen, setCountrySelectOpen] = useState(false);

  const toggleCountrySelectOpen = e => {
    e.preventDefault();
    setTimeout(() => setCountrySelectOpen(prevState => !prevState), 100);
  };

  const closeCountrySelect = () => {
    countrySelectOpen !== false && setCountrySelectOpen(false);
  };

  const dropDownRef = useDetectClickOutside({
    onTriggered: closeCountrySelect
  });

  const onCountryChange = country => {
    const rest = currentValue.slice(1 + currentCountry.dialCode.length);
    setCurrentValue(`+${country?.dialCode}${rest}`);
    setCurrentCountry(country);
  };

  const onUpdateCurrentNumberOrEmail = value => {
    if (phoneIsValid(value) && value?.indexOf('+') === 0) {
      const countryGuess = guessCountryByPartialPhoneNumber({
        phone: value,
        countries: defaultCountries,
        currentCountryIso2: currentCountry?.iso2
      });
      if (countryGuess.country) {
        setCurrentCountry(countryGuess.country);
      }
    }
    const newValue = updateCurrentNumberOrEmail(value, currentCountry);
    setCurrentValue(newValue);
    props.onChange(newValue);
  };

  useEffect(() => {
    setCurrentValue(
      updateCurrentNumberOrEmail(props.value || '', currentCountry)
    );
  }, [props.value, currentCountry]);

  const valueNotValid = Boolean(
    currentValue &&
      (props.onlyPhone
        ? !phoneIsValid(currentValue)
        : !userEmailOrMobileIsValid(currentValue))
  );

  return (
    <ListInput
      id={props.id}
      readonly={props.readonly}
      style={{ listStyle: 'none' }}
      className={
        'withEditStartSlot userFields ' + (props.readonly ? ' readonly' : '')
      }
      outline
      floatingLabel
      errorMessageForce={valueNotValid}
      errorMessage={
        valueNotValid
          ? II18.get(
              props.onlyPhone ? 'forms_wrong_phone' : 'forms_wrong_email_phone'
            )
          : null
      }
      required
      label={props.label}
      type="text"
      placeholder={props.placeholder}
      clearButton={!props.readonly}
      value={currentValue}
      onChange={e => onUpdateCurrentNumberOrEmail(e.target.value.trim())}
      onInputClear={e => {
        e.preventDefault();
        setTimeout(() => onUpdateCurrentNumberOrEmail(''), 50);
      }}
    >
      {currentValue && phoneIsValid(currentValue) ? (
        <div slot={'input'}>
          <div
            className="country-select"
            onClick={e => toggleCountrySelectOpen(e)}
          >
            <FlagImage iso2={currentCountry?.iso2} size="30px" />
          </div>

          <div
            className={'dropdown-list ' + (countrySelectOpen ? 'visible' : '')}
            ref={dropDownRef}
          >
            {defaultCountries.map(c => {
              const country = parseCountry(c);
              return (
                <div
                  className={
                    'dropdown-list-item ' +
                    (currentCountry?.iso2 === country.iso2 ? 'selected' : '')
                  }
                  key={country.iso2}
                  onClick={() => {
                    onCountryChange(country);
                    closeCountrySelect();
                  }}
                >
                  <FlagImage iso2={country.iso2} size="30px" />
                  <span className="title">{country.name}</span>
                  <span className="info">{`+${country.dialCode}`}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <Icon
          color={'gray'}
          slot={'input'}
          material={props.onlyPhone ? 'smartphone' : 'devices'}
        />
      )}
    </ListInput>
  );
}

EmailPhoneInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  region: PropTypes.string,
  value: PropTypes.string,
  onlyPhone: PropTypes.bool,
  readonly: PropTypes.bool,
  onChange: PropTypes.func
};
